import React, { useState, useEffect } from 'react';
import { TrendingUp, CreditCard, Clock, CheckCircle, ArrowRight, Wallet } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommissionWidget = ({ partnerId, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (partnerId && token) {
      loadCommissionData();
    }
  }, [partnerId, token]);

  const loadCommissionData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/partners/commission-summary/${partnerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (error) {
      console.error('Error loading commission data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white animate-pulse">
        <div className="h-6 bg-white/20 rounded w-32 mb-4"></div>
        <div className="h-10 bg-white/20 rounded w-48"></div>
      </div>
    );
  }

  if (!data) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  return (
    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white shadow-lg" data-testid="commission-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5" />
          <span className="font-semibold">Provisionen</span>
        </div>
        <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
          {data.commission_level_name} ({data.provision_level}%)
        </span>
      </div>

      {/* Main Stats */}
      <div className="mb-6">
        <p className="text-sm text-white/70 mb-1">Dieser Monat</p>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold">{formatCurrency(data.this_month)}</span>
          {data.this_month_cases > 0 && (
            <span className="text-sm text-white/70">({data.this_month_cases} Fälle)</span>
          )}
        </div>
      </div>

      {/* Breakdown */}
      <div className="space-y-3">
        {/* Ready for Payout */}
        {data.ready_for_payout > 0 && (
          <div className="flex items-center justify-between bg-white/10 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-400/20 rounded-lg flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-green-300" />
              </div>
              <div>
                <p className="text-sm font-medium">Bereit zur Auszahlung</p>
                <p className="text-xs text-white/60">{data.counts.ready} Fall/Fälle</p>
              </div>
            </div>
            <span className="font-bold text-green-300">{formatCurrency(data.ready_for_payout)}</span>
          </div>
        )}

        {/* Pending */}
        {data.pending > 0 && (
          <div className="flex items-center justify-between bg-white/10 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-yellow-400/20 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-yellow-300" />
              </div>
              <div>
                <p className="text-sm font-medium">In Bearbeitung</p>
                <p className="text-xs text-white/60">{data.counts.pending} Fall/Fälle</p>
              </div>
            </div>
            <span className="font-medium text-yellow-300">{formatCurrency(data.pending)}</span>
          </div>
        )}

        {/* Total Paid */}
        <div className="flex items-center justify-between bg-white/10 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-medium">Bereits ausgezahlt</p>
              <p className="text-xs text-white/60">{data.counts.paid} Fall/Fälle</p>
            </div>
          </div>
          <span className="font-medium">{formatCurrency(data.total_paid)}</span>
        </div>
      </div>

      {/* Total Earned */}
      <div className="mt-4 pt-4 border-t border-white/20">
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/70">Gesamt verdient</span>
          <span className="text-xl font-bold">{formatCurrency(data.total_earned)}</span>
        </div>
      </div>
    </div>
  );
};

export default CommissionWidget;
